import { useCallback, useEffect, useState } from 'react';
import { useServices } from '../../app/services';
import { getMeta, setMeta } from '../../data/appMeta';
import { monthsEnding, shiftMonth, type MonthKey } from '../../domain/analytics';
import { findAnomalies, type Anomaly } from '../../domain/anomaly';
import { spendingPace, type Pace } from '../../domain/pace';
import type { Transaction } from '../../domain/model';
import { billRows } from '../../domain/recurring';
import { spendingOf } from '../../domain/spending';

/** "Not unusual" choices, kept on this device (a household member may see it differently). */
export const DISMISSED_KEY = 'anomalies.dismissed';
const DISMISSED_LIMIT = 200;

export interface MonthInsights {
  month: MonthKey;
  /** Only for the month in progress. */
  pace: Pace | null;
  anomalies: Anomaly[];
  dismissed: ReadonlySet<string>;
  /** Spending of the six months ending with this one (null before the budget's first month). */
  trend: { month: MonthKey; total: number | null }[];
}

/** Monthly spending totals for a run of months, from entries (months before `firstDate` are null, not 0). */
export function monthTrend(entries: readonly Transaction[], months: readonly MonthKey[], firstDate: string | null) {
  const totals = new Map<MonthKey, number>();
  for (const e of entries) totals.set(e.date.slice(0, 7), (totals.get(e.date.slice(0, 7)) ?? 0) + spendingOf(e));
  return months.map((month) => ({ month, total: firstDate && month >= firstDate.slice(0, 7) ? Math.max(0, totals.get(month) ?? 0) : null }));
}

/**
 * What Home says about a month beyond its totals (V1.5): the pace of the
 * month in progress and what is out of the ordinary. One read of the
 * twelve months before and the month itself serves both.
 */
export function useMonthInsights(month: MonthKey, today: string) {
  const { store, db, activeSpace, dataVersion } = useServices();
  const [insights, setInsights] = useState<MonthInsights | null>(null);
  const [dismissed, setDismissed] = useState<string[] | null>(null);

  useEffect(() => {
    let live = true;
    void getMeta(db, DISMISSED_KEY).then((raw) => {
      if (!live) return;
      try {
        const list: unknown = raw ? JSON.parse(raw) : [];
        setDismissed(Array.isArray(list) ? list.filter((k): k is string => typeof k === 'string') : []);
      } catch {
        setDismissed([]);
      }
    });
    return () => {
      live = false;
    };
  }, [db]);

  useEffect(() => {
    if (!activeSpace) return;
    let live = true;
    const current = today.slice(0, 7) === month;
    void Promise.all([
      store.transactions.listRange(activeSpace.id, `${shiftMonth(month, -12)}-01`, current ? today : `${month}-31`),
      store.recurring.listForSpace(activeSpace.id),
      // "Last time" for a variable bill: two calendar years back, like the bills list.
      current ? store.recurring.linkedEntries(activeSpace.id, `${Number(today.slice(0, 4)) - 2}-01-01`, '9999-12-31') : Promise.resolve([]),
      store.transactions.firstDate(activeSpace.id),
    ]).then(([entries, templates, linked, firstDate]) => {
      if (!live) return;
      setInsights({
        month,
        pace: current
          ? spendingPace({ today, entries, bills: billRows(templates, linked, today), targetMinor: activeSpace.monthlyTargetMinor, firstDate })
          : null,
        anomalies: findAnomalies({ month, today, entries, templates: new Map(templates.map((t) => [t.id, t])), firstDate }),
        dismissed: new Set(),
        trend: monthTrend(entries, monthsEnding(month, 6), firstDate),
      });
    });
    return () => {
      live = false;
    };
  }, [store, activeSpace, dataVersion, month, today]);

  const dismiss = useCallback(
    (key: string) => {
      const next = [...(dismissed ?? []).filter((k) => k !== key), key].slice(-DISMISSED_LIMIT);
      setDismissed(next);
      void setMeta(db, DISMISSED_KEY, JSON.stringify(next));
    },
    [db, dismissed],
  );

  // Stale months never show: switching month shows nothing until the new one is read.
  if (!insights || insights.month !== month || dismissed === null) return null;
  return { ...insights, dismissed: new Set(dismissed), dismiss };
}
